import { FileWarning, Binary, FolderTree, CopyCheck } from "lucide-react";
import { AppColorVariant } from "../theme/AppColorVariant";

export enum ConflictReason {
  SizeMismatch = "sizeMismatch",
  HashMismatch = "hashMismatch",
  PathCollision = "pathCollision",
  // A path collision the client proved is byte-identical; handled like PathCollision but shown distinctly
  // (amber) so the operator knows adopting one winner and accepting the rest is lossless.
  PathCollisionDuplicate = "pathCollisionDuplicate",
}

export const ConflictReasonLabels: Record<ConflictReason, string> = {
  [ConflictReason.SizeMismatch]: "Size Mismatch",
  [ConflictReason.HashMismatch]: "Hash Mismatch",
  [ConflictReason.PathCollision]: "Path Collision",
  [ConflictReason.PathCollisionDuplicate]: "Path Duplicate",
};

export const ConflictReasonColors: Record<ConflictReason, AppColorVariant> = {
  [ConflictReason.SizeMismatch]: "warning",
  [ConflictReason.HashMismatch]: "warning",
  [ConflictReason.PathCollision]: "danger",
  // Amber, not the red of a genuine collision: the contents match, so it is safe to reconcile.
  [ConflictReason.PathCollisionDuplicate]: "warning",
};

export const ConflictReasonIcons: Record<ConflictReason, React.ElementType> = {
  [ConflictReason.SizeMismatch]: Binary,
  [ConflictReason.HashMismatch]: FileWarning,
  [ConflictReason.PathCollision]: FolderTree,
  [ConflictReason.PathCollisionDuplicate]: CopyCheck,
};

/** Short operator-facing explanation of each reason — shown in the legend popover and per-badge tooltips. */
export const ConflictReasonDescriptions: Record<ConflictReason, string> = {
  [ConflictReason.SizeMismatch]:
    "The local file and the SharePoint copy differ in size — often from SharePoint re-packaging Office documents, but any genuine content change will also trigger it.",
  [ConflictReason.HashMismatch]:
    "The SharePoint copy and the local file have different contents (different hash).",
  [ConflictReason.PathCollision]:
    "Two different files are competing for the same SharePoint path — one must win or be renamed.",
  [ConflictReason.PathCollisionDuplicate]:
    "Files claim the same path but are byte-identical, so adopting one and accepting the rest is lossless.",
};

/** SizeMismatch and HashMismatch are "content mismatch" conflicts, which share the same allowed resolution actions. */
export const isContentMismatch = (reason: ConflictReason): boolean =>
  reason === ConflictReason.SizeMismatch || reason === ConflictReason.HashMismatch;

/** A path-collision reason (contents differ, or the byte-identical duplicate) — new local files contend for one target path. */
export const isPathCollision = (reason: ConflictReason): boolean =>
  reason === ConflictReason.PathCollision ||
  reason === ConflictReason.PathCollisionDuplicate;
